/**
 * Locale-aware date formatting. Generic (takes a string + a locale, knows no domain type), so
 * it belongs in core rather than in a module.
 *
 * Everything routes through `Intl`, which means French and English render their own month
 * names — no hand-built month tables, no `toLocaleDateString()`
 * calls scattered across features with drifting options.
 */

/**
 * Map an app locale to the BCP-47 tag used for formatting.
 *
 * `en` alone formats month-first ("Aug 12, 2026") because `Intl` assumes US English. This is an
 * Algerian platform: dates read day-first in French, and the design writes
 * "12 Aug → 30 Sep 2026", so English is formatted as `en-GB` to match its siblings. Without this
 * the same screen shows two different date orders depending on the chosen language.
 */
function formattingLocale(locale: string): string {
  return locale === "en" || locale.startsWith("en-US") ? "en-GB" : locale;
}

/** Options shared by every date rendering in the app: "12 Aug 2026". */
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

/** Same, plus 24-hour time: "12 Aug 2026, 09:00". */
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTIONS,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/** Format an ISO instant as a date. Returns `null` for a missing or unparsable value. */
export function formatDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(formattingLocale(locale), DATE_OPTIONS).format(
    date
  );
}

/**
 * Format two ISO instants as a range: `12 Aug → 30 Sep 2026`.
 *
 * The year is printed once when both ends share it, which is how the design reads and what keeps
 * the line from wrapping in a narrow card. Returns `null` if either end is missing or unparsable,
 * so the caller can fall back to a placeholder.
 */
export function formatDateRange(
  fromIso: string | null,
  toIso: string | null,
  locale: string
): string | null {
  if (!fromIso || !toIso) return null;
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  const sameYear = from.getFullYear() === to.getFullYear();
  const fromOptions: Intl.DateTimeFormatOptions = sameYear
    ? { day: "2-digit", month: "short" }
    : DATE_OPTIONS;

  const start = new Intl.DateTimeFormat(
    formattingLocale(locale),
    fromOptions
  ).format(from);
  const end = new Intl.DateTimeFormat(
    formattingLocale(locale),
    DATE_OPTIONS
  ).format(to);
  return `${start} → ${end}`;
}

/** Format an ISO instant as a date and 24-hour time. */
export function formatDateTime(
  iso: string | null,
  locale: string
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(
    formattingLocale(locale),
    DATE_TIME_OPTIONS
  ).format(date);
}

/**
 * Day and month only, day first (`01/11`): the compact date of the invitation rows. French and
 * English both read day/month here (`en-GB` order), as the frames draw it.
 */
export function formatDayMonth(
  iso: string | null,
  locale: string
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(
    formattingLocale(locale) === "fr-FR" ? "fr-FR" : "en-GB",
    {
      day: "2-digit",
      month: "2-digit",
    }
  ).format(date);
}
